/**
 * Admin: Companies (SA-1456 spec §7.2), the deployment's reference list of companies: customers,
 * forwarders, brokers, shippers, consignees and our own firms. A manager corrects a company, removes a
 * spelling, or merges two rows that are one firm (`CompanyDrawer`).
 *
 * The page shows only to a holder of `companies:manage` on a deployment where a module uses companies
 * (the manifest's `companies_page`), both read strictly: nothing renders until both stores have loaded,
 * then anyone else goes home. The role chips filter the loaded list in the browser (Our firms is a carrier
 * or an affiliate); `?merged=1` adds the merged rows, each read as "merged into <survivor>". A merged row's
 * counts are its own, and its survivor's already include them, so the page never sums the two.
 */
import { useMemo, useState } from 'react'
import { Navigate, useSearchParams } from 'react-router-dom'
import {
  Alert, AlertDescription, Button, Spinner, Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui'
import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import { FilterChip } from '@/components/inbox/FilterChip'
import { tourAnchor } from '@/components/tour/tourAnchor'
import { roleLabel } from '@/components/companies/companyText'
import { useCompanyList } from '@/hooks/useCompanies'
import { useFeatureStore } from '@/stores/featureStore'
import { usePermissionStore } from '@/stores/permissionStore'
import type { Company } from '@/types/companies'
import { CompanyDrawer } from './CompanyDrawer'
import * as T from './companiesText'

const OUR_FIRMS = ['carrier', 'affiliate']

/** A company shows under a chip when it holds the role (Our firms: a carrier or an affiliate). */
function matchesFilter(company: Company, filter: T.FilterId): boolean {
  if (filter === 'all') return true
  if (filter === 'ours') return company.roles.some((r) => OUR_FIRMS.includes(r))
  return company.roles.includes(filter)
}

const QUIET = 'text-sm text-muted-foreground'

function RoleChips({ roles }: { roles: string[] }) {
  if (roles.length === 0) return <span className={QUIET}>{T.NONE}</span>
  return (
    <span className="flex flex-wrap gap-1">
      {roles.map((r) => (
        <span key={r} className="rounded border border-border px-1.5 text-xs text-muted-foreground">{roleLabel(r)}</span>
      ))}
    </span>
  )
}

function Spellings({ company }: { company: Company }) {
  if (company.spellings.length === 0) return <span className={QUIET}>{T.NO_SPELLINGS}</span>
  return (
    <details>
      <summary className="cursor-pointer text-sm">{T.spellingsCount(company.spellings.length)}</summary>
      <ul className="mt-1 space-y-0.5 text-sm text-muted-foreground">
        {company.spellings.map((s) => <li key={s.id} className="[overflow-wrap:anywhere]">{s.text}</li>)}
      </ul>
    </details>
  )
}

function CompanyRow({ company, survivor, onOpen }: {
  company: Company
  survivor: Company | null
  onOpen: (company: Company) => void
}) {
  const usage = T.usageText(company.usage)
  const into = company.merged_into
  return (
    <TableRow className="align-top hover:bg-accent">
      <TableCell>
        {into ? (
          <div className="space-y-0.5">
            <span className="font-medium text-muted-foreground">{company.name}</span>
            <div className="text-xs">
              {survivor ? (
                <button type="button" className="text-primary hover:underline" onClick={() => onOpen(survivor)}>
                  {T.mergedInto(into.name)}
                </button>
              ) : (
                <span className="text-muted-foreground">{T.mergedInto(into.name)}</span>
              )}
            </div>
          </div>
        ) : (
          <button type="button" aria-label={T.editLabel(company.name)}
            className="text-left font-medium text-primary hover:underline" onClick={() => onOpen(company)}>
            {company.name}
          </button>
        )}
      </TableCell>
      <TableCell>{company.short_name ?? <span className={QUIET}>{T.NONE}</span>}</TableCell>
      <TableCell>{company.legal_name ?? <span className={QUIET}>{T.NONE}</span>}</TableCell>
      <TableCell>{company.country ?? <span className={QUIET}>{T.NONE}</span>}</TableCell>
      <TableCell><RoleChips roles={company.roles} /></TableCell>
      <TableCell><Spellings company={company} /></TableCell>
      <TableCell>{usage || <span className={QUIET}>{T.NOTHING_NAMES}</span>}</TableCell>
    </TableRow>
  )
}

function CompaniesDirectory() {
  const [params, setParams] = useSearchParams()
  const merged = params.get('merged') === '1'
  const [filter, setFilter] = useState<T.FilterId>('all')
  const [opened, setOpened] = useState<Company | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const list = useCompanyList(merged)
  const rows = list.data
  const live = useMemo(() => (rows ?? []).filter((c) => !c.merged_into), [rows])
  const byId = useMemo(() => new Map(live.map((c) => [c.id, c])), [live])
  const names = useMemo(() => new Map(live.map((c) => [c.id, c.name])), [live])
  const shown = (rows ?? []).filter((c) => matchesFilter(c, filter))

  const setMerged = (on: boolean) => {
    const next = new URLSearchParams(params)
    if (on) next.set('merged', '1')
    else next.delete('merged')
    setParams(next, { replace: true })
  }

  const open = (company: Company) => { setNotice(null); setOpened(company) }

  return (
    <PageFrame width="list" className="space-y-4">
      <PageHeader title={T.PAGE_TITLE} subtitle={T.PAGE_SENTENCE} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div role="group" aria-label={T.FILTER_GROUP} {...tourAnchor('companies.filters')} className="flex flex-wrap gap-1.5">
          {T.FILTERS.map((f) => (
            <FilterChip key={f.id} on={filter === f.id} onClick={() => setFilter(f.id)}>{f.label}</FilterChip>
          ))}
        </div>
        <label {...tourAnchor('companies.merged')} className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" className="accent-primary" checked={merged} onChange={(e) => setMerged(e.target.checked)} />
          {T.SHOW_MERGED}
        </label>
      </div>
      {merged && <p className={QUIET}>{T.MERGED_NOTE}</p>}
      <p role="status" className="text-sm">{notice ?? ''}</p>
      <div {...tourAnchor('companies.list')}>
        {rows === undefined ? (
          list.isError ? (
            <Alert variant="destructive"><AlertDescription>{T.LIST_UNAVAILABLE}</AlertDescription></Alert>
          ) : (
            <div className="flex justify-center py-6"><Spinner /></div>
          )
        ) : rows.length === 0 ? (
          <p className={QUIET}>{T.EMPTY}</p>
        ) : shown.length === 0 ? (
          <div className="flex items-center gap-3">
            <p className={QUIET}>{T.EMPTY_FILTERED}</p>
            <Button type="button" variant="outline" size="sm" onClick={() => setFilter('all')}>{T.FILTERS[0].label}</Button>
          </div>
        ) : (
          <Table aria-label={T.LIST_LABEL} containerClassName="rounded-lg border border-section-border">
            <TableHeader>
              <TableRow>
                <TableHead>{T.COLUMNS.name}</TableHead>
                <TableHead>{T.COLUMNS.short}</TableHead>
                <TableHead>{T.COLUMNS.legal}</TableHead>
                <TableHead>{T.COLUMNS.country}</TableHead>
                <TableHead>{T.COLUMNS.roles}</TableHead>
                <TableHead>{T.COLUMNS.spellings}</TableHead>
                <TableHead>{T.COLUMNS.usage}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((c) => (
                <CompanyRow key={c.id} company={c} onOpen={open}
                  survivor={c.merged_into ? byId.get(c.merged_into.id) ?? null : null} />
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      {opened && (
        <CompanyDrawer key={opened.id} company={opened} names={names} onClose={() => setOpened(null)} onOpen={open}
          onMerged={(from, into) => { setOpened(null); setNotice(T.merged(from, into)) }} />
      )}
    </PageFrame>
  )
}

export function CompaniesPage() {
  const featuresLoaded = useFeatureStore((s) => s.isLoaded)
  const companiesPage = useFeatureStore((s) => s.companiesPage)
  const permissionsLoaded = usePermissionStore((s) => s.isLoaded)
  const canManage = usePermissionStore((s) => s.permissions.has('companies:manage'))
  if (!featuresLoaded || !permissionsLoaded) return null
  if (!companiesPage || !canManage) return <Navigate to="/" replace />
  return <CompaniesDirectory />
}
